import React from 'react'
import { IoMic } from 'react-icons/io5'
import useLocaleText from 'components/useLocaleText'
import { AI_FEED_OPTIONS, AIFeedOption, pulseAnimation } from '../constants'
import { useSpeechInput } from '../hooks/useSpeechInput'
import {
  ModalTitle,
  ModalSubtitle,
  StyledTextarea,
  TextareaWithMic,
  MicButton,
  AIFeedOptionsContainer,
  AIFeedOptionRow,
  RadioCircle,
  RadioDot,
  AIFeedOptionContent,
  AIFeedOptionTitle,
  AIFeedOptionDescription,
  CollapsedOptionsStack,
  StackedOptionCard,
  StackedOptionTitle,
} from '../styles'

interface AIFeedStepProps {
  selectedOption: AIFeedOption
  setSelectedOption: (option: AIFeedOption) => void
  customInstruction: string
  setCustomInstruction: (value: string) => void
  isOpen: boolean
}

export const AIFeedStep: React.FC<AIFeedStepProps> = ({
  selectedOption,
  setSelectedOption,
  customInstruction,
  setCustomInstruction,
  isOpen,
}) => {
  const ai_popup_title = useLocaleText('ai_popup_title', true)
  const ai_popup_desc = useLocaleText('ai_popup_desc', true)
  const ai_popup_placeholder = useLocaleText('ai_popup_placeholder', true)
  const t_go_other_options = useLocaleText('go_other_options', true)

  const {
    listening,
    speechSupported,
    isMicrophoneAvailable,
    startListening,
    stopListening,
    handleManualInput,
    cleanup,
  } = useSpeechInput({
    value: customInstruction,
    onChange: setCustomInstruction,
    isOpen: isOpen && selectedOption === 'custom',
  })

  const isCustomSelected = selectedOption === 'custom'
  const otherOptions = AI_FEED_OPTIONS.filter((opt) => opt.id !== 'custom')

  // Handle clicking on collapsed stack - deselect custom and show all options
  const handleStackClick = () => {
    cleanup() // Stop mic when collapsing
    setSelectedOption('top-managers')
  }

  // Handle selecting custom option
  const handleCustomSelect = () => {
    setSelectedOption('custom')
  }

  // Handle selecting other option
  const handleOtherSelect = (optionId: AIFeedOption) => {
    cleanup() // Stop mic when switching options
    setSelectedOption(optionId)
  }

  return (
    <>
      <ModalTitle>{ai_popup_title}</ModalTitle>
      <ModalSubtitle>{ai_popup_desc}</ModalSubtitle>

      {isCustomSelected && (
        <TextareaWithMic pos="relative">
          <style>{pulseAnimation}</style>
          <StyledTextarea
            placeholder={ai_popup_placeholder}
            value={customInstruction}
            onChange={(e: any) => {
              handleManualInput()
              setCustomInstruction(e.target.value)
            }}
            style={{ marginBottom: 0 }}
          />
          {speechSupported && (
            <MicButton
              position="absolute"
              bottom="-20px"
              left="50%"
              px={0}
              transform="translateX(-50%)"
              onClick={listening ? stopListening : startListening}
              isDisabled={isMicrophoneAvailable === false}
              isListening={listening}
              title={
                isMicrophoneAvailable === false
                  ? 'Microphone is not available'
                  : listening
                  ? 'Stop recording'
                  : 'Start recording'
              }
            >
              <IoMic size={26} />
            </MicButton>
          )}
        </TextareaWithMic>
      )}

      <AIFeedOptionsContainer>
        {isCustomSelected ? (
          <>
            {/* Stacked cards of other options */}
            <CollapsedOptionsStack onClick={handleStackClick}>
              {otherOptions.map((option, index) => (
                <StackedOptionCard key={option.id} index={index} total={otherOptions.length}>
                  <RadioCircle isSelected={false} />
                  <StackedOptionTitle>{t_go_other_options}</StackedOptionTitle>
                </StackedOptionCard>
              ))}
            </CollapsedOptionsStack>
          </>
        ) : (
          /* All options visible */
          AI_FEED_OPTIONS.map((option) => (
            <AIFeedOptionRow
              key={option.id}
              isSelected={selectedOption === option.id}
              onClick={() => (option.id === 'custom' ? handleCustomSelect() : handleOtherSelect(option.id))}
            >
              <RadioCircle isSelected={selectedOption === option.id}>
                {selectedOption === option.id && <RadioDot />}
              </RadioCircle>
              <AIFeedOptionContent>
                <AIFeedOptionTitle>{option.title}</AIFeedOptionTitle>
                <AIFeedOptionDescription>{option.description}</AIFeedOptionDescription>
              </AIFeedOptionContent>
            </AIFeedOptionRow>
          ))
        )}
      </AIFeedOptionsContainer>
    </>
  )
}
